/**
 * EventDiscussButton — the labelled «Обсудить» action on event cards
 * (CORE-1696). Replaces the small two-bubble icon on the web cards: the
 * calendar card (/lives), the announcement card in the master's feed and the
 * finished-recording card. Tap opens the «Чаты мастера» tab straight on the
 * event's discussion (see openEventDiscussion); it never creates a discussion
 * and never touches the event RSVP.
 *
 * Two shapes, per the approved mockups:
 *  - `outline` — a ghost pill with icon + text, sits next to the primary CTA
 *    on desktop and goes full-width under it on narrow screens;
 *  - `row` — a full-width accent row with icon, text and a chevron (mobile
 *    feed, v2 mockup).
 *
 * Renders nothing when the event has no discussion to open (no master ref or
 * a non-event kind such as an uploaded practice).
 */
import React from 'react';
import { ActivityIndicator, Platform, StyleProp, StyleSheet, Text, TouchableOpacity, View, ViewStyle } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { BrandToast } from '../BrandToast';
import { fontWeights } from '../../styles/typographyTokens';
import { DISCUSSION_UNAVAILABLE_MESSAGE, type EventDiscussionRef, useEventDiscussion } from './useEventDiscussion';

const SOLAR = '#E98245';

interface Props extends EventDiscussionRef {
  variant?: 'outline' | 'row';
  /** Stretch to the container width (stacked mobile layouts). */
  fullWidth?: boolean;
  isDark: boolean;
  label?: string;
  accentColor?: string;
  textColor: string;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

export const EventDiscussButton: React.FC<Props> = ({
  masterUserId,
  refId,
  linkedEntityType,
  masterProfileId,
  discussionId,
  announced,
  masterName,
  variant = 'outline',
  fullWidth = false,
  isDark,
  label = 'Обсудить',
  accentColor = SOLAR,
  textColor,
  style,
  testID,
}) => {
  const { available, busy, unavailable, dismissUnavailable, onPress } = useEventDiscussion({
    masterUserId,
    refId,
    linkedEntityType,
    masterProfileId,
    discussionId,
    announced,
    masterName,
  });

  if (!available) return null;

  const isRow = variant === 'row';
  const surface = {
    backgroundColor: isDark ? 'rgba(255,244,237,0.055)' : 'rgba(255,250,247,0.94)',
    borderColor: isDark ? 'rgba(255,176,128,0.32)' : 'rgba(233,130,69,0.38)',
  };

  return (
    <>
      <TouchableOpacity
        onPress={onPress}
        disabled={busy}
        activeOpacity={0.78}
        style={[isRow ? styles.row : styles.pill, surface, fullWidth && styles.fullWidth, style]}
        accessibilityRole="button"
        accessibilityLabel="Открыть обсуждение события"
        testID={testID ?? 'event-discuss-button'}
      >
        <View style={styles.content}>
          {busy ? (
            <ActivityIndicator size="small" color={accentColor} />
          ) : (
            <Ionicons name="chatbubble-ellipses-outline" size={isRow ? 18 : 16} color={accentColor} />
          )}
          <Text style={[styles.label, { color: textColor }]} numberOfLines={1}>{label}</Text>
        </View>
        {isRow ? <Ionicons name="chevron-forward" size={16} color={accentColor} /> : null}
      </TouchableOpacity>
      {Platform.OS === 'web' && (
        <BrandToast
          visible={unavailable}
          onHide={dismissUnavailable}
          message={DISCUSSION_UNAVAILABLE_MESSAGE}
          iconName="chatbubbles-outline"
          iconColor={accentColor}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  pill: {
    minHeight: 40,
    paddingHorizontal: 16,
    borderRadius: 999,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({ web: { cursor: 'pointer', transition: 'all 0.2s ease' } as any }),
  },
  row: {
    minHeight: 44,
    paddingHorizontal: 14,
    borderRadius: 14,
    borderWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    ...Platform.select({ web: { cursor: 'pointer', transition: 'all 0.2s ease' } as any }),
  },
  fullWidth: {
    alignSelf: 'stretch',
    width: '100%',
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    flexShrink: 1,
  },
  label: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    letterSpacing: 0.2,
  },
});

export default EventDiscussButton;
